import { APRData } from 'components/modules/IslandList/IslandListItem/APRColumn'
import { ROUTES } from 'config/routes'
import { useTokens } from 'hooks/Tokens'
import { useMemo } from 'react'
import { StakingTokenType } from 'types/farms'
import { ApiVaultData, BaultPosition, FarmPosition, IslandPosition, Token } from 'types/portfolio'
import { formatNumber } from 'utils/formatCurrencyAmount'
import { formatDollarAmount } from 'utils/portfolio/formatDollar'
import { multiplyBigIntByNumber } from 'utils/portfolio/multiplyBigIntByNumber'
import { formatUnits } from 'viem'

export interface UnderlyingTokenAmounts {
  token0AmountFormatted: string
  token1AmountFormatted: string
  token0Amount: string
  token1Amount: string
}

export type CompactPositionEntry =
  | { id: string; type: 'Island'; value: number; position: IslandPosition }
  | { id: string; type: 'Bault'; value: number; position: BaultPosition }
  | { id: string; type: 'Farm'; value: number; position: FarmPosition }

export interface RewardDisplayItem {
  key: string
  symbol: string
  amountFormatted: string
  usdValue: string
}

export interface CompactPositionAggregatedData {
  totalToken0: number
  totalToken1: number
  aprData: APRData | null
  unclaimedRewardsDisplay: RewardDisplayItem[]
  totalToken0Display: string
  totalToken1Display: string
}

interface UseCompactPositionRowModelArgs {
  vault: ApiVaultData
  baultPositions?: BaultPosition[]
  islandPosition?: IslandPosition
  farmPositions?: FarmPosition[]
  baultUnderlyingBalances?: UnderlyingTokenAmounts
  islandUnderlyingBalances?: UnderlyingTokenAmounts
  v2UnderlyingBalances?: UnderlyingTokenAmounts
  charmUnderlyingBalances?: UnderlyingTokenAmounts
  farmUnderlyingBalances?: Record<string, UnderlyingTokenAmounts>
}

interface UseCompactPositionRowModelResult {
  totalValue: number
  aggregatedData: CompactPositionAggregatedData | null
  allPositions: CompactPositionEntry[]
  hasExpandableContent: boolean
  manageRoute: string
  claimModalType: 'farmClaim' | 'vaultClaim' | null
}

export function useCompactPositionRowModel({
  vault,
  baultPositions,
  islandPosition,
  farmPositions,
  baultUnderlyingBalances,
  islandUnderlyingBalances,
  v2UnderlyingBalances,
  charmUnderlyingBalances,
  farmUnderlyingBalances,
}: UseCompactPositionRowModelArgs): UseCompactPositionRowModelResult {
  const farmRewardTokenAddresses = useMemo(() => {
    if (!farmPositions || farmPositions.length === 0) return []
    const addresses = farmPositions.flatMap(
      (farm) => farm.farm.rewardTokens?.map((token) => token.address || token.id) || []
    )
    return Array.from(new Set(addresses.filter(Boolean)))
  }, [farmPositions])

  const farmRewardTokens = useTokens(farmRewardTokenAddresses)
  const farmRewardTokenList = useMemo(() => farmRewardTokens.filter(Boolean), [farmRewardTokens])

  const totalValue = useMemo(
    () =>
      (vault.vaultBalanceUSD || 0) +
      (vault.baultBalanceUSD || 0) +
      (vault.farmBalanceUSD || 0) +
      (vault.externalBalanceUSD || 0),
    [vault.vaultBalanceUSD, vault.baultBalanceUSD, vault.farmBalanceUSD, vault.externalBalanceUSD]
  )

  const aggregatedData = useMemo(() => {
    if (!vault.token0 || !vault.token1) return null
    let totalToken0Raw = 0n
    let totalToken1Raw = 0n
    let totalToken0Human = 0
    let totalToken1Human = 0
    const unclaimedRewardsMap = new Map<string, { token: Token; amount: bigint; amountUSD: number }>()

    const token0Decimals = vault.token0.decimals || 0
    const token1Decimals = vault.token1.decimals || 0

    const toRawAmount = (amount: string) => {
      if (!amount) return 0n
      try {
        return BigInt(amount)
      } catch {
        return 0n
      }
    }

    const addUnderlyingTotals = (
      balances: { token0Amount: string; token1Amount: string },
      options?: { isHumanUnits?: boolean; priceMultiplier?: number }
    ) => {
      const isHumanUnits = options?.isHumanUnits ?? false
      const priceMultiplier = options?.priceMultiplier

      if (isHumanUnits) {
        const token0Human = Number(balances.token0Amount || 0)
        const token1Human = Number(balances.token1Amount || 0)
        totalToken0Human += priceMultiplier !== undefined ? token0Human * priceMultiplier : token0Human
        totalToken1Human += priceMultiplier !== undefined ? token1Human * priceMultiplier : token1Human
        return
      }

      let token0Raw = toRawAmount(balances.token0Amount)
      let token1Raw = toRawAmount(balances.token1Amount)

      if (priceMultiplier !== undefined) {
        token0Raw = multiplyBigIntByNumber(token0Raw, priceMultiplier)
        token1Raw = multiplyBigIntByNumber(token1Raw, priceMultiplier)
      }

      totalToken0Raw += token0Raw
      totalToken1Raw += token1Raw
    }

    if (islandPosition && Number(islandPosition.balance) > 0) {
      let underlyingBalances = islandUnderlyingBalances
      if (vault.provider === StakingTokenType.V2) {
        underlyingBalances = v2UnderlyingBalances
      } else if (vault.provider === StakingTokenType.CHARM) {
        underlyingBalances = charmUnderlyingBalances
      }

      if (underlyingBalances) {
        addUnderlyingTotals(underlyingBalances, { isHumanUnits: vault.provider === StakingTokenType.V2 })
      }
    }

    if (baultPositions && baultPositions.length > 0) {
      baultPositions.forEach((bault) => {
        if (baultUnderlyingBalances) {
          addUnderlyingTotals(baultUnderlyingBalances, { priceMultiplier: bault.bault.priceVault })
        }
      })
    }

    if (farmPositions && farmPositions.length > 0) {
      farmPositions.forEach((farm) => {
        farm.claimableRewards?.forEach((reward) => {
          const rewardToken = reward.token
          const rewardKey = (rewardToken.address || rewardToken.id || '').toLowerCase()
          if (!rewardKey) return

          const existing = unclaimedRewardsMap.get(rewardKey) || {
            token: rewardToken,
            amount: 0n,
            amountUSD: 0,
          }

          const rewardAmount = reward.amount ? BigInt(reward.amount) : 0n
          existing.amount += rewardAmount
          existing.amountUSD += reward.amountUSD || 0
          unclaimedRewardsMap.set(rewardKey, existing)
        })

        const farmBalances =
          vault.provider === StakingTokenType.V2 ? v2UnderlyingBalances : farmUnderlyingBalances?.[farm.farm.id]

        if (farmBalances) {
          addUnderlyingTotals(farmBalances, { isHumanUnits: vault.provider === StakingTokenType.V2 })
        }
      })
    }

    const hasIslandPosition = !!(islandPosition && Number(islandPosition.balance) > 0)
    const hasBaultPosition = !!baultPositions?.some((bault) => Number(bault.balance) > 0)
    const hasFarmPosition = !!farmPositions?.some((farm) => Number(farm.stakedAmount) > 0 || farm.stakedAmountUSD > 0)
    const hasAnyPosition = hasIslandPosition || hasBaultPosition || hasFarmPosition
    const unclaimedRewards = Array.from(unclaimedRewardsMap.values()).filter((reward) => reward.amount > 0n)

    if (totalToken0Raw === 0n && totalToken1Raw === 0n && !hasAnyPosition) {
      return null
    }

    const totalToken0 = Number(formatUnits(totalToken0Raw, token0Decimals)) + totalToken0Human
    const totalToken1 = Number(formatUnits(totalToken1Raw, token1Decimals)) + totalToken1Human

    const showSwapFeeAPR = hasIslandPosition
    const showRewardTokens = hasFarmPosition
    const swapFeeAPR = vault.apr ?? 0

    let farmAPR: number | undefined
    if (hasFarmPosition && farmPositions && farmPositions.length > 0) {
      const firstFarmWithApr = farmPositions.find((farm) => farm.apr !== undefined && farm.apr !== null)
      farmAPR = firstFarmWithApr?.apr ?? 0
    }

    let vaultAPY: number | undefined
    if (hasBaultPosition && baultPositions && baultPositions.length > 0) {
      const firstBaultWithApy = baultPositions.find(
        (bault) => bault.bault.apy !== undefined && bault.bault.apy !== null
      )
      vaultAPY = firstBaultWithApy?.bault.apy ?? 0
    }

    const aprData: APRData | null = hasAnyPosition
      ? {
          swapFeeAPR,
          farmAPR,
          vaultAPY,
          activeRewardTokens: showRewardTokens ? farmRewardTokenList : [],
          rewardTokens: showRewardTokens ? farmRewardTokenList : [],
          showSwapFeeAPR,
          showRewardTokens,
          isGeneric: false,
          isRewardVault: false,
        }
      : null

    const unclaimedRewardsDisplay = unclaimedRewards.map((reward) => ({
      key: reward.token.address || reward.token.id,
      symbol: reward.token.symbol,
      amountFormatted: formatNumber(reward.amount, reward.token.decimals) || '0',
      usdValue: formatDollarAmount(reward.amountUSD || 0),
    }))

    return {
      totalToken0,
      totalToken1,
      aprData,
      unclaimedRewardsDisplay,
      totalToken0Display: formatNumber(totalToken0) || '0',
      totalToken1Display: formatNumber(totalToken1) || '0',
    }
  }, [
    vault.token0,
    vault.token1,
    vault.apr,
    vault.provider,
    farmRewardTokenList,
    islandPosition,
    baultPositions,
    farmPositions,
    islandUnderlyingBalances,
    baultUnderlyingBalances,
    farmUnderlyingBalances,
    charmUnderlyingBalances,
    v2UnderlyingBalances,
  ])

  const allPositions = useMemo(() => {
    const positions: CompactPositionEntry[] = []

    if (islandPosition && Number(islandPosition.balance) > 0) {
      positions.push({
        id: `island-${vault.id}`,
        type: 'Island',
        value: islandPosition.balanceUSD,
        position: islandPosition,
      })
    }

    if (baultPositions && baultPositions.length > 0) {
      baultPositions.forEach((baultPosition, index) => {
        positions.push({
          id: `bault-${baultPosition.bault.id}-${index}`,
          type: 'Bault',
          value: baultPosition.balanceUSD,
          position: baultPosition,
        })
      })
    }

    if (farmPositions && farmPositions.length > 0) {
      farmPositions.forEach((farmPosition, index) => {
        positions.push({
          id: `farm-${farmPosition.farm.id}-${index}`,
          type: 'Farm',
          value: farmPosition.stakedAmountUSD,
          position: farmPosition,
        })
      })
    }

    return positions
  }, [islandPosition, baultPositions, farmPositions, vault.id])

  const manageRoute = useMemo(() => ROUTES.islands.detail(vault.id, vault.farm?.id), [vault.id, vault.farm?.id])
  const kodiakFarm = farmPositions?.find((farm) => farm.provider === 'kodiak')?.farm
  const rewardVaultFarm = farmPositions?.find((farm) => farm.provider === 'bgt')?.farm
  const claimModalType = rewardVaultFarm ? 'vaultClaim' : kodiakFarm ? 'farmClaim' : null

  return {
    totalValue,
    aggregatedData,
    allPositions,
    hasExpandableContent: allPositions.length > 0,
    manageRoute,
    claimModalType,
  }
}
